import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, errMsg, useFetch } from "../lib/api";
import { PageHeader, Loading } from "../components/bits";
import { ChatThread } from "../components/ChatThread";
import { AddToCalendar } from "../components/AddToCalendar";
import { Confirm } from "../components/Confirm";

function NurseActions({ m, reload }) {
  const { sp, refreshBadges } = useApp();
  const send = async (response) => {
    try {
      await api.post(sp(`/missions/${m.id}/respond`), { response });
      toast.success(response === "dispo" ? "Merci ! Le responsable est prévenu." : "C'est noté");
      reload();
      refreshBadges();
    } catch (e) { toast.error(errMsg(e)); reload(); }
  };
  if (m.is_requester) {
    return <div className="rounded-2xl bg-teal-50 p-5 text-center font-semibold text-teal-900" data-testid="replacement-pending-msg">{m.state === "open" ? "🔁 Ta demande de remplacement est en cours. Le responsable validera la remplaçante." : "✅ Remplacement validé."}</div>;
  }
  if (m.state === "cancelled") return <div className="rounded-2xl bg-slate-100 p-5 text-center font-semibold text-slate-600" data-testid="mission-cancelled-msg">Mission annulée.</div>;
  if (m.is_mine) {
    return (
      <div className="space-y-3">
        <div className="rounded-2xl bg-emerald-600 p-5 text-center text-xl font-bold text-white" data-testid="mission-confirmed-banner">✅ Mission confirmée</div>
        <AddToCalendar title={`Mission ${m.home_name}`} date={m.date} start={m.start} end={m.end} query={`mission=${m.id}`} />
      </div>
    );
  }
  if (m.filled) return <div className="rounded-2xl bg-slate-100 p-5 text-center font-semibold text-slate-600" data-testid="mission-filled-msg">Cette mission est déjà pourvue.</div>;
  return (
    <div className="space-y-3">
      <button onClick={() => send("dispo")} data-testid="mission-dispo-btn"
        className={`tap w-full rounded-2xl text-lg font-extrabold uppercase ${m.my_response === "dispo" ? "bg-emerald-700 text-white ring-4 ring-emerald-200" : "bg-emerald-600 text-white"} min-h-[64px]`}>
        {m.my_response === "dispo" ? "✓ Je suis disponible" : "Je suis disponible"}
      </button>
      <button onClick={() => send("indispo")} data-testid="mission-indispo-btn"
        className={`tap w-full rounded-2xl border text-base font-bold uppercase ${m.my_response === "indispo" ? "border-slate-700 bg-slate-700 text-white" : "border-slate-200 bg-white text-slate-700"} min-h-[56px]`}>
        Pas disponible
      </button>
    </div>
  );
}

function ManagerActions({ m, reload }) {
  const { sp, user } = useApp();
  const nav = useNavigate();
  const [ask, setAsk] = useState(null);
  const selfName = `${user.first_name} ${user.last_name}`;
  const assign = async () => {
    try { await api.post(sp(`/missions/${m.id}/assign`), { user_id: ask.user_id }); toast.success(ask.self ? "Mission ajoutée à votre planning" : `Mission attribuée à ${ask.name.split(" ")[0]}`); setAsk(null); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const unassign = async (uid) => { try { await api.post(sp(`/missions/${m.id}/unassign/${uid}`)); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const cancel = async () => {
    if (!window.confirm("Annuler cette mission ?")) return;
    try { await api.post(sp(`/missions/${m.id}/cancel`)); toast.success("Mission annulée"); nav("/missions"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      {m.state === "cancelled" && <div className="mb-4 rounded-2xl bg-slate-200 p-4 text-center font-bold text-slate-600">Mission annulée</div>}
      {m.state === "done" && <div className="mb-4 rounded-2xl bg-slate-100 p-4 text-center font-bold text-slate-600">Terminée</div>}
      {m.filled && m.state !== "done" && <div className="mb-4 rounded-2xl bg-emerald-600 p-4 text-center text-lg font-extrabold text-white" data-testid="mission-filled-badge">✅ POURVUE</div>}
      {m.assigned.length > 0 && (
        <div className="mb-5 space-y-2">
          {m.assigned.map((u, i) => (
            <div key={u} className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="mission-assigned-item">
              <span className="font-bold text-emerald-900">✓ {m.assigned_names[i]}</span>
              <button onClick={() => unassign(u)} className="rounded-lg p-2 text-emerald-800 hover:bg-emerald-100" aria-label="Retirer" data-testid="mission-unassign-btn"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      {m.is_mine && <div className="mb-5"><AddToCalendar title={`Mission ${m.home_name}`} date={m.date} start={m.start} end={m.end} query={`mission=${m.id}`} /></div>}
      {!m.filled && !m.is_mine && m.state === "open" && (
        <button className="btn-soft mb-5 w-full !min-h-[56px]" onClick={() => setAsk({ user_id: user.id, name: selfName, self: true })} data-testid="mission-self-assign-btn">Je prends cette mission</button>
      )}
      {!m.filled && m.state === "open" && (
        <>
          <h2 className="eyebrow mb-2">Disponibles</h2>
          <div className="mb-3 space-y-2" data-testid="mission-dispo-list">
            {m.dispo.length === 0 && <p className="card p-4 text-sm text-slate-500">En attente de réponses…</p>}
            {m.dispo.map((p) => (
              <button key={p.user_id} onClick={() => setAsk(p)} data-testid={`mission-dispo-${p.user_id}`}
                className="card tap flex w-full items-center gap-3 px-4 text-left text-lg font-bold">✅ {p.name}</button>
            ))}
          </div>
          <p className="mb-6 text-xs text-slate-500">{m.no_response} sans réponse{m.indispo.length ? ` · pas disponibles : ${m.indispo.join(", ")}` : ""}</p>
        </>
      )}
      {m.state === "open" && <button className="mb-6 w-full py-2 text-sm font-semibold text-red-600" onClick={cancel} data-testid="mission-cancel-btn">Annuler la mission</button>}
      <Confirm open={!!ask} title={ask ? (ask.self ? "Vous attribuer cette mission ?" : `Attribuer la mission à ${ask.name.split(" ")[0]} ?`) : ""} onCancel={() => setAsk(null)} onConfirm={assign} />
    </>
  );
}

export default function MissionDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { sp, isManager } = useApp();
  const { data: m, reload, error } = useFetch(sp(`/missions/${id}`), 8000);
  const [chat, setChat] = useState(params.get("chat") === "1");
  if (error) return <p className="text-slate-500">{error} <Link to="/missions" className="text-teal-700">Retour</Link></p>;
  if (!m) return <Loading />;
  return (
    <div data-testid="mission-detail-page">
      <PageHeader title={m.kind === "replacement" ? `Remplacement de ${m.replaced_name?.split(" ")[0] || ""}` : m.title} subtitle={m.kind === "replacement" ? m.home_name : m.title !== m.home_name ? m.home_name : null} back="/missions" />
      <div className="card mb-6 p-5 fade-up">
        <div className="font-display text-xl font-extrabold">{m.label_date}</div>
        {m.start && <div className="mt-1 font-mono text-lg text-teal-700">{m.start} → {m.end}</div>}
        <div className="mt-2 text-slate-600">{m.needed} infirmière{m.needed > 1 ? "s" : ""} recherchée{m.needed > 1 ? "s" : ""}</div>
        {m.comment && <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">{m.comment}</p>}
      </div>
      <div className="mb-6">{isManager ? <ManagerActions m={m} reload={reload} /> : <NurseActions m={m} reload={reload} />}</div>
      {chat ? <ChatThread mid={m.id} /> : (
        <button className="mx-auto flex items-center gap-2 py-3 text-sm font-semibold text-slate-500" onClick={() => setChat(true)} data-testid="mission-messages-toggle">
          <MessageCircle className="h-4 w-4" /> {isManager ? "Messages" : "Poser une question"}
        </button>
      )}
    </div>
  );
}
